import type {
	BizyActionEvent,
	BizyChannelEvent,
	BizyCompleteEvent,
	BizyNodeAppendedEvent,
	BizyNodeTokenEvent,
	BizyProactiveConversationStartedEvent,
	BizySupportEscalatedEvent,
	BizySupportHandoffEvent,
	BizySupportResolvedEvent,
	BizyToolCallEvent,
} from './types/channel';

/**
 * Callback bag for Bizy channel events.
 *
 * Pass this to `dispatchBizyChannelEvent`. The caller is responsible for
 * wiring the transport (ActionCable, IPC, etc.) and calling dispatch with
 * each received message. `activeRequestId` is passed as a plain value
 * (not a React ref) so this module stays React-free.
 */
export interface BizyChannelOptions {
	onToken?: (content: string) => void;
	onComplete?: (data: BizyCompleteEvent['data']) => void;
	onError?: (message: string) => void;
	onToolCall?: (data: BizyToolCallEvent['data']) => void;
	onPreliminaryToken?: (content: string) => void;
	onCancelled?: (content: string) => void;
	onPageAction?: (data: BizyActionEvent['data']) => void;
	onSupportHandoff?: (data: BizySupportHandoffEvent['data']) => void;
	onSupportResolved?: (data: BizySupportResolvedEvent['data']) => void;
	onSupportEscalated?: (data: BizySupportEscalatedEvent['data']) => void;
	/** Incremental delta for an open markdown node on the typed
	 *  assistant_message event stream. */
	onNodeToken?: (data: BizyNodeTokenEvent['data']) => void;
	/** Complete card or one-shot markdown node on the typed
	 *  assistant_message event stream. */
	onNodeAppended?: (data: BizyNodeAppendedEvent['data']) => void;
	/** Fires when Bizy starts a conversation the user did not initiate. */
	onProactiveConversationStarted?: (data: BizyProactiveConversationStartedEvent['data']) => void;
	onConnected?: () => void;
	onDisconnected?: () => void;
	/** Fires when another tab's request completes (cross-tab sync). */
	onOtherTabComplete?: (data: BizyCompleteEvent['data']) => void;
	/** Fires when another tab is actively streaming (cross-tab awareness). */
	onOtherTabLoading?: () => void;
	/** Fires when another tab's request errors (cross-tab sync — clears loading). */
	onOtherTabError?: () => void;
}

/**
 * Routes a raw BizyChannel message to the appropriate callback in `options`.
 *
 * Handles cross-tab isolation: if `activeRequestId` is provided and the
 * message carries a different `request_id`, the event is routed to the
 * `onOtherTab*` callbacks instead of the primary ones. Support events
 * (`support_handoff`, `support_resolved`, `support_escalated`) are always
 * routed to their primary callbacks regardless of request_id.
 */
export function dispatchBizyChannelEvent(message: BizyChannelEvent, options: BizyChannelOptions, activeRequestId?: string): void {
	if (!message) return;

	const eventRequestId = 'request_id' in message.data ? message.data.request_id : undefined;
	const isOtherTab = activeRequestId && eventRequestId ? eventRequestId !== activeRequestId : false;

	const isSupportEvent = message.event === 'support_handoff' || message.event === 'support_resolved' || message.event === 'support_escalated';

	if (isOtherTab && !isSupportEvent) {
		if (message.event === 'complete') {
			options.onOtherTabComplete?.(message.data);
		} else if (message.event === 'error' || message.event === 'cancelled') {
			options.onOtherTabError?.();
		} else if (message.event === 'token' || message.event === 'tool_call' || message.event === 'node_token' || message.event === 'node_appended') {
			options.onOtherTabLoading?.();
		}
		return;
	}

	switch (message.event) {
		case 'token':
			options.onToken?.(message.data.content);
			break;
		case 'complete':
			options.onComplete?.(message.data);
			break;
		case 'error':
			options.onError?.(message.data.message);
			break;
		case 'tool_call':
			options.onToolCall?.(message.data);
			break;
		case 'preliminary_token':
			options.onPreliminaryToken?.(message.data.content);
			break;
		case 'cancelled':
			options.onCancelled?.(message.data.content);
			break;
		case 'page_action':
			options.onPageAction?.(message.data);
			break;
		case 'support_handoff':
			options.onSupportHandoff?.(message.data);
			break;
		case 'support_resolved':
			options.onSupportResolved?.(message.data);
			break;
		case 'support_escalated':
			options.onSupportEscalated?.(message.data);
			break;
		case 'node_token':
			options.onNodeToken?.(message.data);
			break;
		case 'node_appended':
			options.onNodeAppended?.(message.data);
			break;
		case 'proactive_conversation_started':
			options.onProactiveConversationStarted?.(message.data);
			break;
	}
}
